import { supabase } from './SupabaseConfig';
import { AuditLogRepository, SecurityLog, StaffActivityLog, PosAuditLog } from '../../domain/audit/ports/AuditLogRepository';
import { InfrastructureError } from '@core/errors/AppError';

export class SupabaseAuditLogRepository implements AuditLogRepository {
  async createSecurityLog(ownerId: string, log: SecurityLog): Promise<string> {
    try {
      const { data, error } = await supabase
        .from('security_logs')
        .insert({
          owner_id: ownerId,
          user_id: log.userId,
          user_name: log.userName,
          location_id: log.locationId,
          action: log.action,
          severity: log.severity,
          status: log.status,
          type: log.type,
          meta: log.meta,
          user_agent: log.userAgent,
          ip_address: log.ipAddress,
          old_value: log.oldValue,
          new_value: log.newValue,
          created_at: new Date().toISOString()
        })
        .select('id')
        .single();

      if (error) throw new InfrastructureError(`Failed to create security log: ${error.message}`, 'SupabaseAuditLogRepository');
      return data.id;
    } catch (err) {
      console.error('[SupabaseAuditLogRepository] createSecurityLog error:', err);
      throw err;
    }
  }

  listenSecurityLogs(ownerId: string, callback: (logs: SecurityLog[]) => void, limitCount: number = 50): () => void {
    const subscription = supabase
      .channel(`security_logs_owner_${ownerId}`)
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'security_logs',
        filter: `owner_id=eq.${ownerId}`
      }, async () => {
        // Fetch last N logs when a change occurs
        const { data, error } = await supabase
          .from('security_logs')
          .select('*')
          .eq('owner_id', ownerId)
          .order('created_at', { ascending: false })
          .limit(limitCount);
        
        if (!error && data) {
          callback(data.map(this.mapToEntity));
        }
      })
      .subscribe();

    // Initial fetch
    supabase
      .from('security_logs')
      .select('*')
      .eq('owner_id', ownerId)
      .order('created_at', { ascending: false })
      .limit(limitCount)
      .then(({ data, error }) => {
        if (!error && data) callback(data.map(this.mapToEntity));
      });

    return () => {
      subscription.unsubscribe();
    };
  }

  async createStaffActivityLog(ownerId: string, log: StaffActivityLog): Promise<string> {
    try {
      const { data, error } = await supabase
        .from('staff_activity_logs')
        .insert({
          owner_id: ownerId,
          staff_id: log.staffId,
          action: log.action,
          label: log.label,
          payload: log.payload,
          created_at: new Date().toISOString()
        })
        .select('id')
        .single();

      if (error) throw new InfrastructureError(`Failed to create staff activity log: ${error.message}`, 'SupabaseAuditLogRepository');
      return data.id;
    } catch (err) {
      console.error('[SupabaseAuditLogRepository] createStaffActivityLog error:', err);
      throw err;
    }
  }

  async getStaffActivityLogs(ownerId: string, staffId?: string, maxItems: number = 50): Promise<StaffActivityLog[]> {
    try {
      let query = supabase
        .from('staff_activity_logs')
        .select('*')
        .eq('owner_id', ownerId)
        .order('created_at', { ascending: false })
        .limit(maxItems);

      if (staffId) {
        query = query.eq('staff_id', staffId);
      }

      const { data, error } = await query;
      if (error) throw new InfrastructureError(`Failed to fetch staff activity logs: ${error.message}`, 'SupabaseAuditLogRepository');
      
      return data.map(d => ({
        id: d.id,
        staffId: d.staff_id,
        action: d.action,
        label: d.label,
        payload: d.payload,
        createdAt: d.created_at
      }));
    } catch (err) {
      console.error('[SupabaseAuditLogRepository] getStaffActivityLogs error:', err);
      throw err;
    }
  }

  listenStaffActivityLogs(ownerId: string, callback: (logs: StaffActivityLog[]) => void, limitCount: number = 50): () => void {
    const subscription = supabase
      .channel(`staff_activity_logs_owner_${ownerId}`)
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'staff_activity_logs',
        filter: `owner_id=eq.${ownerId}`
      }, async () => {
        const logs = await this.getStaffActivityLogs(ownerId, undefined, limitCount);
        callback(logs);
      })
      .subscribe();

    // Initial fetch
    this.getStaffActivityLogs(ownerId, undefined, limitCount).then(callback);

    return () => {
      subscription.unsubscribe();
    };
  }

  async createPosAuditLog(ownerId: string, log: Omit<PosAuditLog, 'id' | 'timestamp'>): Promise<string> {
    try {
      const { data, error } = await supabase
        .from('pos_audit_logs')
        .insert({
          owner_id: ownerId,
          action_type: log.actionType,
          location_id: log.locationId,
          location_name: log.locationName,
          staff_id: log.staffId,
          staff_name: log.staffName,
          details: log.details,
          severity: log.severity,
          review_status: log.reviewStatus,
          owner_note: log.ownerNote,
          approved_at: log.approvedAt,
          approved_by: log.approvedBy,
          created_at: new Date().toISOString()
        })
        .select('id')
        .single();

      if (error) throw new InfrastructureError(`Failed to create pos audit log: ${error.message}`, 'SupabaseAuditLogRepository');
      return data.id;
    } catch (err) {
      console.error('[SupabaseAuditLogRepository] createPosAuditLog error:', err);
      throw err;
    }
  }

  async updatePosAuditLog(ownerId: string, logId: string, updates: Partial<PosAuditLog>): Promise<void> {
    try {
      const payload: Record<string, any> = { updated_at: new Date().toISOString() };
      if (updates.reviewStatus) payload.review_status = updates.reviewStatus;
      if (updates.ownerNote !== undefined) payload.owner_note = updates.ownerNote;
      if (updates.approvedBy) payload.approved_by = updates.approvedBy;
      if (updates.approvedAt) payload.approved_at = updates.approvedAt;

      const { error } = await supabase
        .from('pos_audit_logs')
        .update(payload)
        .eq('id', logId)
        .eq('owner_id', ownerId);

      if (error) throw new InfrastructureError(`Failed to update pos audit log: ${error.message}`, 'SupabaseAuditLogRepository');
    } catch (err) {
      console.error('[SupabaseAuditLogRepository] updatePosAuditLog error:', err);
      throw err;
    }
  }

  listenPosAuditLogs(ownerId: string, callback: (logs: PosAuditLog[]) => void, options: { locationId?: string, limitCount?: number } = {}): () => void {
    const { locationId, limitCount = 100 } = options;
    const subscription = supabase
      .channel(`pos_audit_logs_owner_${ownerId}`)
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'pos_audit_logs',
        filter: `owner_id=eq.${ownerId}`
      }, async () => {
        const logs = await this.getPosAuditLogs(ownerId, { locationId, limitCount });
        callback(logs);
      })
      .subscribe();

    // Initial fetch
    this.getPosAuditLogs(ownerId, { locationId, limitCount }).then(callback);

    return () => {
      subscription.unsubscribe();
    };
  }

  async getPosAuditLogs(ownerId: string, options: { locationId?: string, limitCount?: number, beforeTimestamp?: string } = {}): Promise<PosAuditLog[]> {
    const { locationId, limitCount = 100, beforeTimestamp } = options;
    
    let query = supabase
      .from('pos_audit_logs')
      .select('*')
      .eq('owner_id', ownerId)
      .order('created_at', { ascending: false })
      .limit(limitCount);

    if (locationId) {
      query = query.eq('location_id', locationId);
    }

    if (beforeTimestamp) {
      query = query.lt('created_at', beforeTimestamp);
    }

    const { data, error } = await query;
    if (error) throw new InfrastructureError(`Failed to fetch pos audit logs: ${error.message}`, 'SupabaseAuditLogRepository');
    
    return (data || []).map(d => this.mapToPosEntity(d));
  }

  private mapToPosEntity(d: any): PosAuditLog {
    const base = {
      id: d.id,
      locationId: d.location_id,
      locationName: d.location_name,
      staffId: d.staff_id,
      staffName: d.staff_name,
      severity: d.severity || 'low',
      reviewStatus: d.review_status || 'pending',
      ownerNote: d.owner_note,
      approvedAt: d.approved_at,
      approvedBy: d.approved_by,
      timestamp: d.created_at
    };

    const actionType = d.action_type as any;
    const details = d.details || {};

    // Basic structure enforcement based on actionType
    return {
      ...base,
      actionType: actionType || 'OTHER',
      details: details
    } as PosAuditLog;
  }

  private mapToEntity(d: any): SecurityLog {
    return {
      id: d.id,
      userId: d.user_id,
      userName: d.user_name,
      locationId: d.location_id,
      ownerId: d.owner_id,
      action: d.action,
      severity: d.severity,
      status: d.status,
      type: d.type,
      meta: d.meta,
      timestamp: d.created_at,
      userAgent: d.user_agent,
      ipAddress: d.ip_address,
      oldValue: d.old_value,
      newValue: d.new_value
    };
  }

}
